import React, { useRef, useState } from "react";
import { hexToRgba, contrastColor } from "../utils";
import { parseDate } from "./Calendar";

// Touch-first calendar: tap a day to anchor a selection, tap another to
// complete the range, long-press for day options. No drag & drop — that
// stays desktop-only.
export default function MobileCalendar({
  data,
  anchorDate,
  onDayTap,
  onDayLongPress,
}) {
  const [showPastMonths, setShowPastMonths] = useState(false);
  const pressRef = useRef({ timer: null, fired: false });

  const now = new Date();
  const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(
    2,
    "0"
  )}-${String(now.getDate()).padStart(2, "0")}`;
  const cutoffMonth =
    data.year < now.getFullYear()
      ? 12
      : data.year > now.getFullYear()
      ? 0
      : now.getMonth();

  function startPress(date) {
    const p = pressRef.current;
    p.fired = false;
    p.timer = setTimeout(() => {
      p.fired = true;
      p.timer = null;
      if (navigator.vibrate) navigator.vibrate(10);
      onDayLongPress && onDayLongPress(date);
    }, 450);
  }
  function cancelPress() {
    const p = pressRef.current;
    if (p.timer) clearTimeout(p.timer);
    p.timer = null;
  }
  function handleClick(date) {
    const p = pressRef.current;
    if (p.fired) {
      p.fired = false;
      return;
    }
    cancelPress();
    onDayTap(date);
  }

  const months = [];
  for (let m = 0; m < 12; m++) {
    const first = new Date(data.year, m, 1).getDay();
    const daysInMonth = new Date(data.year, m + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < first; i++) cells.push({ empty: true });
    for (let d = 1; d <= daysInMonth; d++) {
      const date = `${data.year}-${String(m + 1).padStart(2, "0")}-${String(
        d
      ).padStart(2, "0")}`;
      cells.push({ date, day: d, ev: data.events[date] });
    }
    months.push({
      m,
      name: new Date(data.year, m, 1).toLocaleString(undefined, {
        month: "long",
      }),
      cells,
    });
  }

  const displayedMonths = showPastMonths
    ? months
    : months.filter((month) => month.m >= cutoffMonth);

  return (
    <div>
      {cutoffMonth > 0 && (
        <div className="calendar-toolbar">
          <button
            className="small-btn"
            onClick={() => setShowPastMonths((s) => !s)}
          >
            {showPastMonths ? "Hide Previous Months" : "Show Previous Months"}
          </button>
        </div>
      )}
      <div className="mobile-months">
        {displayedMonths.map((month) => (
          <div key={month.m} className="month">
            <h3 className="month-title">
              {month.name} {data.year}
            </h3>
            <div className="weekdays">
              {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
                <div key={i}>{d}</div>
              ))}
            </div>
            <div className="days">
              {month.cells.map((cell, i) => {
                if (cell.empty) return <div key={i} className="day inactive" />;
                const ev = cell.ev;
                const date = cell.date;
                const bg =
                  ev && ev.catId
                    ? data.categories.find((c) => c.id === ev.catId)?.color ||
                      undefined
                    : undefined;
                const weekday = parseDate(date).getDay();
                const isWeekend = weekday === 0 || weekday === 6;
                const prevCell = month.cells[i - 1];
                const nextCell = month.cells[i + 1];
                const prevIsWeekend =
                  prevCell &&
                  !prevCell.empty &&
                  (parseDate(prevCell.date).getDay() === 0 ||
                    parseDate(prevCell.date).getDay() === 6);
                const nextIsWeekend =
                  nextCell &&
                  !nextCell.empty &&
                  (parseDate(nextCell.date).getDay() === 0 ||
                    parseDate(nextCell.date).getDay() === 6);
                const leftConnected =
                  prevCell && !prevCell.empty && (prevCell.ev || prevIsWeekend);
                const rightConnected =
                  nextCell && !nextCell.empty && (nextCell.ev || nextIsWeekend);
                const connClass =
                  leftConnected && rightConnected
                    ? " connected-both"
                    : leftConnected
                    ? " connected-left"
                    : rightConnected
                    ? " connected-right"
                    : "";
                const isToday = date === todayStr;
                const isHalf = !!(ev && ev.half && bg);
                const halfBackground = isHalf
                  ? `linear-gradient(135deg, ${bg} 50%, ${hexToRgba(
                      bg,
                      0.35
                    )} 50%)`
                  : undefined;
                return (
                  <div
                    key={date}
                    data-date={date}
                    className={
                      "day" +
                      (ev ? " filled" : "") +
                      connClass +
                      (isWeekend ? " weekend" : "") +
                      (isToday ? " today" : "") +
                      (isHalf ? " half" : "") +
                      (anchorDate === date ? " anchor" : "")
                    }
                    onTouchStart={() => startPress(date)}
                    onTouchMove={cancelPress}
                    onTouchEnd={cancelPress}
                    onTouchCancel={cancelPress}
                    onContextMenu={(e) => e.preventDefault()}
                    onClick={() => handleClick(date)}
                    style={{ background: halfBackground || bg || undefined }}
                  >
                    <div
                      className="date-num"
                      style={{ color: ev && bg ? contrastColor(bg) : undefined }}
                    >
                      {cell.day}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
